import React from 'react'
import { Empty, Table, Icon } from 'antd'
import { useDraggable } from '@dnd-kit/core'
import { useTranslation } from 'react-i18next'
import AdCard from './AdCard'
import RowActions from './RowActions'
import { formatDate } from '../utils/adapters'
import { getSimilarAspectRatio } from '../../../../services/display'
import AdGridHolder from '../styles/adGridStyle'

const TYPE_ICON = {
  image: 'picture',
  video: 'video-camera',
}

const DraggableAdCard = ({ dragData, disabled, cardProps }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `ad-${cardProps.ad.id}`,
    data: dragData,
    disabled,
  })

  const style = {
    ...(transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 10 } : {}),
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <AdCard {...cardProps} dragHandleProps={disabled ? undefined : { ...listeners, ...attributes }} />
    </div>
  )
}

const AdGrid = ({
  ads = [],
  viewMode = 'grid',
  selectedIds = [],
  onToggleSelect,
  onSetSelection,
  getMenuItems,
  onPreview,
  showStatus = false,
  getActions,
  getAdded,
  getDragData,
  extraColumns = [],
  renderRowActions,
  columnLabels = {},
  showTableTitle = true,
  cardMinWidth = 200,
}) => {
  const { t } = useTranslation()

  if (!ads.length) {
    return (
      <AdGridHolder>
        <Empty description={t('No ads yet')} />
      </AdGridHolder>
    )
  }

  if (viewMode === 'list') {
    const columns = [
      {
        title: columnLabels.name || t('Name'),
        key: 'name',
        render: (_, ad) => (
          <div
            className="cell-thumb-name"
            onClick={() => onPreview && onPreview(ad)}
            role="button"
          >
            {ad.thumbnail ? (
              <img src={ad.thumbnail} alt={ad.name} className="row-thumb" />
            ) : (
              <span className="row-thumb placeholder">
                <Icon type={TYPE_ICON[ad.type] || 'file'} />
              </span>
            )}
            <span className="row-name">{ad.name}</span>
          </div>
        ),
      },
      {
        title: columnLabels.aspect || t('Aspect'),
        key: 'aspect',
        width: 150,
        render: (_, ad) =>
          ad.width && ad.height ? getSimilarAspectRatio(ad.width, ad.height, t) : '-',
      },
      ...extraColumns,
      {
        title: columnLabels.date || t('Creation date'),
        key: 'date',
        width: 160,
        render: (_, ad) => formatDate(ad.date),
      },
      {
        title: columnLabels.actions || t('Actions'),
        key: 'actions',
        width: 90,
        align: 'center',
        render: (_, ad) =>
          renderRowActions ? renderRowActions(ad) : <RowActions items={getMenuItems ? getMenuItems(ad) : []} />,
      },
    ]

    const rowSelection = onSetSelection
      ? {
          // antd v3 mishandles frozen (seamless-immutable) arrays for selection,
          // so pass a plain mutable copy.
          selectedRowKeys: Array.from(selectedIds),
          onChange: (keys) => onSetSelection(keys),
        }
      : undefined

    return (
      <AdGridHolder className="table-view">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={Array.from(ads)}
          rowSelection={rowSelection}
          pagination={false}
          size="middle"
          title={showTableTitle ? () => t('Ads') : undefined}
        />
      </AdGridHolder>
    )
  }

  return (
    <AdGridHolder
      className="grid-view"
      style={cardMinWidth !== 200 ? { gridTemplateColumns: `repeat(auto-fill, minmax(${cardMinWidth}px, 1fr))` } : undefined}
    >
      {ads.map((ad) => {
        const added = getAdded ? getAdded(ad) : false
        const cardProps = {
          ad,
          viewMode,
          selected: selectedIds.includes(ad.id),
          onToggleSelect: onToggleSelect ? () => onToggleSelect(ad.id) : undefined,
          onPreview: () => onPreview && onPreview(ad),
          menuItems: getMenuItems && getMenuItems(ad),
          showStatus,
          actions: getActions ? getActions(ad) : undefined,
          added,
        }
        return getDragData ? (
          <DraggableAdCard key={ad.id} dragData={getDragData(ad)} disabled={added} cardProps={cardProps} />
        ) : (
          <AdCard key={ad.id} {...cardProps} />
        )
      })}
    </AdGridHolder>
  )
}

export default AdGrid
